import React, { useState, useEffect, useCallback } from 'react';
import { WifiOff, Wifi, CloudOff, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { getQueueCount, getQueuedActions, syncQueue } from '../utils/offlineQueue';

export default function OfflineBanner({ token }) {
  const [online, setOnline] = useState(navigator.onLine);
  const [queueCount, setQueueCount] = useState(0);
  const [queueItems, setQueueItems] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [justSynced, setJustSynced] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const checkQueue = useCallback(async () => {
    try {
      const count = await getQueueCount();
      setQueueCount(count);
      if (count > 0) {
        const items = await getQueuedActions();
        setQueueItems(items);
      } else {
        setQueueItems([]);
        setExpanded(false);
      }
    } catch {}
  }, []);

  useEffect(() => {
    const goOnline = () => { setOnline(true); checkQueue(); };
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    checkQueue();
    const interval = setInterval(checkQueue, 5000);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      clearInterval(interval);
    };
  }, [checkQueue]);

  // Auto-sync when coming back online
  useEffect(() => {
    if (online && queueCount > 0 && token && !syncing) {
      handleSync();
    }
  }, [online, queueCount]);

  const handleSync = async () => {
    if (!token || syncing) return;
    setSyncing(true);
    try {
      const result = await syncQueue(token);
      if (result.synced > 0) {
        toast.success(`${result.synced} operazioni sincronizzate!`);
        setJustSynced(true);
        setTimeout(() => setJustSynced(false), 3000);
      }
      if (result.failed > 0) {
        toast.error(`${result.failed} operazioni non riuscite`);
      }
      checkQueue();
    } catch {
      toast.error('Errore di sincronizzazione');
    } finally {
      setSyncing(false);
    }
  };

  // Nothing to show if online and no queue
  if (online && queueCount === 0 && !justSynced) return null;

  return (
    <div data-testid="offline-banner" className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      !online ? 'bg-[#FF5555]' : queueCount > 0 ? 'bg-[#FFAA00]' : 'bg-[#3ECF8E]'
    }`}>
      <div className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium">
        {!online ? (
          <>
            <WifiOff size={14} className="text-white" />
            <span className="text-white">Sei offline — le operazioni verranno salvate</span>
            {queueCount > 0 && (
              <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-1 bg-white/20 text-white text-xs px-2 py-0.5 rounded-full ml-1">
                {queueCount} in coda {expanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
              </button>
            )}
          </>
        ) : queueCount > 0 ? (
          <>
            <CloudOff size={14} className="text-[#111114]" />
            <span className="text-[#111114]">{queueCount} operazioni da sincronizzare</span>
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-[#111114]/60 ml-1"
              data-testid="expand-queue-btn"
            >
              {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            <button
              onClick={handleSync}
              disabled={syncing}
              data-testid="sync-btn"
              className="flex items-center gap-1 bg-[#111114] text-white text-xs px-3 py-1 rounded-full ml-2 hover:bg-[#222] disabled:opacity-50"
            >
              <RefreshCw size={10} className={syncing ? 'animate-spin' : ''} />
              {syncing ? 'Sync...' : 'Sincronizza'}
            </button>
          </>
        ) : justSynced ? (
          <>
            <Wifi size={14} className="text-[#111114]" />
            <span className="text-[#111114]">Tutto sincronizzato!</span>
          </>
        ) : null}
      </div>
      {/* Expanded queue details */}
      {expanded && queueItems.length > 0 && (
        <div className={`px-4 pb-2 space-y-1 ${!online ? 'text-white/80' : 'text-[#111114]/80'}`} data-testid="queue-details">
          {queueItems.map((item, i) => (
            <div key={item.id || i} className={`text-xs py-1 px-3 rounded-lg ${!online ? 'bg-white/10' : 'bg-black/10'}`}>
              {item.label || item.url}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
